import { createSlice } from '@reduxjs/toolkit';

const defaultFormData = {
  coverIsFor: '-',
  location: '-',
  dateOfBirth: ['-', '-'],
  dependantDateOfBirth: ['29/09/2024'],
  typeOfCover: '-',
  healthFundFeatures: [],
  selectedHospitalBenefits: [],
  selectedExtrasBenefits: [],
  rebatePercentage: '-',
  healthFund: '0.0',
  partnerHealthFund: '0.0',
  tierIndex: null,
  hadContinuousCover: true,
  hadContinuousCoverPartner: true,
  changedLoadingManuallyCustomer: false,
  changedLoadingManuallyPartner: false,
  customerLoading: 0,
  partnerLoading: 0,
  paymentFrequency: 'Fortnightly',
  haveSameColour: true,
  hasHealthFund: 'Yes',
  hasPartnerHealthFund: 'Yes',
};

export type DefaultFormData = typeof defaultFormData;

export const initialState = {
  general_details: {
    cover_start_date: '',
    customer_type: '',
    price: 0,
    lhc_price: 0,
    product_id: '',
    rate_code: '',
    excess: 0,
    health_fund_id: 0,
    product_type: '',
    bupa_services: [],
  },
  customer: {
    title: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    gender: '',
    dob: '',
    age: 0,
    certified_age_of_entry: 0,
    continuous_cover: false,
    email: '',
    phone: '',
    mobile: '',
    previous_fund_code: '',
    previous_fund_number: '',
    authority_transfer_certificate: true,
    is_rebate_declaration_confirmed: false,
    medicare_first_name: '',
    medicare_middle_name: '',
    medicare_last_name: '',
    medicare_position: 1,
    medicare_card_number: '',
    medicare_expiry_day: '',
    medicare_expiry_month: '',
    medicare_expiry_year: 0,
    medicare_card_color: '',
    medicare_eligibility: true,
    corr_meth_id: 'Email',
    is_send_email: '1',
    is_receive_marketing: '1',
    aus_citizen: true,
    agr_question_1:
      'Which level of rebate do you want to apply to your policy?',
    agr_answer_1: 'Base Tier',
    agr_question_2: 'Are you covered by this policy?',
    agr_answer_2: 'Yes',
    agr_question_3: 'Are all people on the policy eligible for Medicare?',
    agr_answer_3: 'Yes',
    agr_question_4:
      'Do you declare that the information that you have provided is complete and correct? Do you understand that giving false or misleading information is a serious offence?',
    agr_answer_4: 'Yes',
  },
  partner: {
    title: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    gender: '',
    dob: '',
    age: 0,
    certified_age_of_entry: 0,
    continuous_cover: false,
    email: '',
    previous_fund_code: '',
    previous_fund_number: '',
    authority_transfer_certificate: false,
    medicare_first_name: '',
    medicare_middle_name: '',
    medicare_last_name: '',
    medicare_position: 2,
    medicare_card_number: '',
    medicare_expiry_day: '',
    medicare_expiry_month: '',
    medicare_expiry_year: 0,
    medicare_card_color: '',
    medicare_eligibility: true,
    partner_authority: true,
  },
  dependants: [
    {
      title: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      gender: '',
      dob: '',
      age: 0,
      certified_age_of_entry: 0,
      previous_fund_code: '',
      previous_fund_number: '',
      education_details: {
        start_date: '',
        completion_date: '',
        institution_code: '',
        institution_name: '',
        student_number: '',
        full_time: false,
      },
      identification_details: {
        medicare_first_name: '',
        medicare_middle_name: '',
        medicare_last_name: '',
        medicare_position: 3,
        medicare_card_number: 0,
        medicare_expiry_day: '',
        medicare_expiry_month: '',
        medicare_expiry_year: 0,
        medicare_card_color: '',
      },
    },
  ],
  residential_address: {
    line_1: '',
    line_2: '',
    suburb: '',
    postcode: 0,
    state: 0,
  },
  postal_address: {
    same_residential: true,
    line_1: '',
    line_2: '',
    suburb: '',
    postcode: 0,
    state: 0,
  },
  government_rebate: {
    income_tier: 0,
    rebate_percentage: 0,
    claiming_rebate: false,
  },
  payment: {
    payment_method: '',
    payment_date: '',
    payment_period: '',
    upfront_payment: false,
    cc_name: '',
    cc_number: '',
    cc_expiry_month: '',
    cc_expiry_year: 0,
    cc_cvv: '',
    cc_type: 'Visa',
    cc_payment_token: '',
    bank_name: '',
    account_name: '',
    bsb_number: '',
    account_number: '',
    allowDirectDebit: true,
  },
};

export type DefaultSalesReqBody = typeof initialState;

const registrationSlice = createSlice({
  name: 'registration',
  initialState: {
    productDetails: null,
    cameBackFromDetails: false,
    cameBackFromApply: false,
    applyDetails: null,
    currentStep: 0,
    isTestMode: false,
    formData: defaultFormData,
    isComparingMode: false,
    salesReqBody: localStorage.getItem('salesReqBody')
      ? JSON.parse(localStorage.getItem('salesReqBody')!)
      : initialState,
    states: [],
    errors: {},
    utmData: {
      campaign_source: '',
      campaign_medium: '',
      ad_group_creative: '',
      landing_page: '',
      conversion_tracking_code: '',
      ip_address: '',
    },
    leadRefNumber: '',
    salesRefNumber: '',
    finalDetails: null,
    healthFunds: [],
    educationalInstitutions: [],
    benefits: [],
    isBenefitsLoading: false,
    isHealthFundLoading: false,
    products: [],
    prefetchedProducts: {},
    compareProducts: [],
    applyStep: -1,
    isKeyFeaturesExpanded: false,
  },
  reducers: {
    setIsComparingMode: (state, action) => {
      state.isComparingMode = action.payload;
    },
    setFinalDetails: (state, action) => {
      state.finalDetails = action.payload;
    },
    setEducationalInstitutions: (state, action) => {
      state.educationalInstitutions = action.payload;
    },
    appendCompareProduct: (state, action) => {
      state.compareProducts.push(action.payload as never);
    },
    removeCompareProduct: (state, action) => {
      state.compareProducts = state.compareProducts.filter(
        (product: any) => product.productId !== action.payload,
      );
    },
    removeAllCompareProducts: (state) => {
      state.compareProducts = [];
    },
    resetFormData: (state) => {
      state.formData = defaultFormData;
    },
    resetSalesReqBody: (state) => {
      state.salesReqBody = initialState;
    },
    setCameBackFromDetails: (state, action) => {
      state.cameBackFromDetails = action.payload;
    },
    setCameBackFromApply: (state, action) => {
      state.cameBackFromApply = action.payload;
    },
    setHealthFunds: (state, action) => {
      state.healthFunds = action.payload;
    },
    setStates: (state, action) => {
      state.states = action.payload;
    },
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    setLeadRefNumber: (state, action) => {
      state.leadRefNumber = action.payload;
    },
    setSalesRefNumber: (state, action) => {
      state.salesRefNumber = action.payload;
    },
    setUtmData: (state, action) => {
      state.utmData = action.payload;
    },
    setTestMode: (state, action) => {
      state.isTestMode = action.payload;
    },
    updateFormData: (state, action) => {
      state.formData = action.payload;
    },
    incrementStep: (state) => {
      state.currentStep += 1;
    },
    decrementStep: (state) => {
      state.currentStep -= 1;
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setProductDetails: (state, action) => {
      state.productDetails = action.payload;
    },
    clearProductDetails: (state) => {
      state.productDetails = null;
    },
    setApplyDetails: (state, action) => {
      state.applyDetails = action.payload;
    },
    clearApplyDetails: (state) => {
      state.applyDetails = null;
    },
    setBenefitsLoading: (state, action) => {
      state.isBenefitsLoading = action.payload;
    },
    setHealthFundLoading: (state, action) => {
      state.isHealthFundLoading = action.payload;
    },
    setProducts: (state, action) => {
      state.products = action.payload;
      if (window.location.search.includes('test=true')) {
        console.log('products updated', state.prefetchedProducts);
      }
    },
    addChild: (state) => {
      state.salesReqBody.dependants = [
        ...state.salesReqBody.dependants,
        {
          title: '',
          first_name: '',
          middle_name: '',
          last_name: '',
          gender: '',
          dob: '29/09/2024',
          age: 0,
          certified_age_of_entry: 0,
          previous_fund_code: '',
          previous_fund_number: '',
          education_details: {
            start_date: '29/09/2024',
            completion_date: '29/09/2024',
            institution_code: '',
            institution_name: '',
            student_number: '',
            full_time: false,
          },
          identification_details: {
            medicare_first_name: '',
            medicare_middle_name: '',
            medicare_last_name: '',
            medicare_position: 1,
            medicare_card_number: 0,
            medicare_expiry_day: '01',
            medicare_expiry_month: '01',
            medicare_expiry_year: 0,
            medicare_card_color: 'Blue',
          },
        },
      ];

      state.formData.dependantDateOfBirth.push('29/09/2024');
    },
    removeChild: (state) => {
      if (state.salesReqBody.dependants.length > 1) {
        state.salesReqBody.dependants.pop();
        state.formData.dependantDateOfBirth.pop();
      }
    },
    removeChildByIndex: (state, action) => {
      state.salesReqBody.dependants.splice(action.payload, 1);
      state.formData.dependantDateOfBirth.splice(action.payload, 1);
    },
    updateSalesReqBody: (state, action) => {
      state.salesReqBody = action.payload;
    },
    setBenefits: (state, action) => {
      state.benefits = action.payload;
    },
    prefetchProducts: (state, action) => {
      const newCache = { ...state.prefetchedProducts, ...action.payload };

      // Implement LRU cache with max 10 pages
      const cacheKeys = Object.keys(newCache);
      if (cacheKeys.length > 10) {
        const sortedKeys = cacheKeys.sort((a, b) => Number(a) - Number(b));
        const keysToRemove = sortedKeys.slice(0, cacheKeys.length - 10);
        keysToRemove.forEach((key) => delete newCache[key]);
      }

      state.prefetchedProducts = newCache;
    },
    setPrefetchProducts: (state, action) => {
      state.prefetchedProducts = action.payload;
      //log out if url contains ?test=true
      if (window.location.search.includes('test=true')) {
        console.log('prefetchedProducts updated', state.prefetchedProducts);
      }
    },

    setApplyStep: (state, action) => {
      state.applyStep = action.payload;
    },
    setIsKeyFeaturesExpanded: (state, action) => {
      state.isKeyFeaturesExpanded = action.payload;
    },
  },
});

export const {
  setIsComparingMode,
  setIsKeyFeaturesExpanded,
  setCameBackFromDetails,
  setEducationalInstitutions,
  updateFormData,
  setCameBackFromApply,
  prefetchProducts,
  setBenefits,
  resetFormData,
  resetSalesReqBody,
  appendCompareProduct,
  removeCompareProduct,
  setSalesRefNumber,
  setFinalDetails,
  setTestMode,
  incrementStep,
  setLeadRefNumber,
  decrementStep,
  setCurrentStep,
  setProductDetails,
  updateSalesReqBody,
  clearProductDetails,
  setApplyDetails,
  setBenefitsLoading,
  setHealthFundLoading,
  clearApplyDetails,
  addChild,
  removeChildByIndex,
  removeChild,
  setUtmData,
  setErrors,
  setStates,
  setProducts,
  setHealthFunds,
  setApplyStep,
  setPrefetchProducts,
  removeAllCompareProducts,
} = registrationSlice.actions;

export default registrationSlice.reducer;
